/**
 * Run Execution Store
 *
 * Global state management for workflow run execution visualization using Zustand.
 * This store manages:
 * - Current run execution state
 * - Individual node execution states
 * - Streaming connection status
 * - Error handling
 */

import type {
  NodeExecutionState,
  NodeExecutionStatus,
  RunExecutionSource,
  NodesExecutionState,
  RunExecutionState,
  RunStatus,
  TerminalRunStatus,
} from "@/types/runStream";
import { isTerminalRunStatus } from "@/types/runStream";
import { create } from "zustand";

/**
 * Helper to check if workflow changes should clear run state.
 * Clears if there's an active run and changes include structural modifications.
 *
 * NOTE: "replace" is intentionally excluded from the default structural types.
 * React Flow v12 emits "replace" changes whenever a node/edge object reference
 * changes (e.g., during apiNodeId enrichment after save). These are data updates,
 * not structural changes, and should NOT clear an active run. Including "replace"
 * caused a race condition where the first run after AI workflow creation was
 * immediately cleared by the node enrichment that happens during save.
 */
export function clearRunOnStructuralChange(
  changes: Array<{ type: string }>,
  structuralTypes: string[] = ["remove", "add"],
): void {
  const hasStructuralChanges = changes.some(c => structuralTypes.includes(c.type));
  if (hasStructuralChanges) {
    useRunExecutionStore.getState().clearRunIfActive();
  }
}

/**
 * Run Execution Store Interface
 */
interface RunExecutionStore {
  // Current run state
  currentRun: RunExecutionState | null;

  // Streaming state
  isStreaming: boolean;

  // Error state
  error: Error | null;

  // Actions - Run lifecycle
  startRun: (runId: string, workflowId?: string, isSimulation?: boolean, source?: RunExecutionSource) => void;
  setRunStatus: (status: RunStatus) => void;
  completeRun: (status: TerminalRunStatus, finishedAt?: string, startedAt?: string) => void;
  clearRun: () => void;
  /** Clears the run only if there's an active run (no-op otherwise) */
  clearRunIfActive: () => void;

  // Actions - Node state
  setNodeStatus: (nodeId: string, status: NodeExecutionStatus, data?: Partial<NodeExecutionState>) => void;
  updateNodeForEachProgress: (nodeId: string, current: number, total: number) => void;
  recordForEachIterationOutcome: (
    nodeId: string,
    current: number,
    total: number,
    outcome: "success" | "failure",
  ) => void;

  // Actions - Streaming
  setStreaming: (isStreaming: boolean) => void;

  // Actions - Error
  setError: (error: Error | null) => void;

  // Selectors
  getNodeStatus: (nodeId: string) => NodeExecutionStatus;
  getNodeState: (nodeId: string) => NodeExecutionState | undefined;
  getCompletedNodeCount: () => number;
  getTotalNodeCount: () => number;

  // Reset
  reset: () => void;
}

/**
 * Initial state for the store
 */
const initialState = {
  currentRun: null,
  isStreaming: false,
  error: null,
};

/**
 * Create default node execution state
 */
function createDefaultNodeState(nodeId: string): NodeExecutionState {
  return {
    status: "idle",
  };
}

/**
 * Run Execution Store
 */
export const useRunExecutionStore = create<RunExecutionStore>((set, get) => ({
  ...initialState,

  // ============================================================================
  // Run Lifecycle Actions
  // ============================================================================

  startRun: (runId: string, workflowId?: string, isSimulation?: boolean, source: RunExecutionSource = "button") => {
    set({
      currentRun: {
        runId,
        workflowId,
        status: "pending",
        startedAt: new Date().toISOString(),
        nodeStates: {},
        isSimulation,
        source,
      },
      isStreaming: true,
      error: null,
    });
  },

  setRunStatus: (status: RunStatus) => {
    set(state => {
      if (!state.currentRun) return state;
      return {
        currentRun: {
          ...state.currentRun,
          status,
        },
      };
    });
  },

  completeRun: (status: TerminalRunStatus, finishedAt?: string, startedAt?: string) => {
    set(state => {
      if (!state.currentRun) return state;
      return {
        currentRun: {
          ...state.currentRun,
          status,
          // Prefer server timestamps for accurate duration display.
          startedAt: startedAt ?? state.currentRun.startedAt,
          finishedAt: finishedAt ?? new Date().toISOString(),
        },
        isStreaming: false,
      };
    });
  },

  clearRun: () => {
    set(initialState);
  },

  clearRunIfActive: () => {
    const state = get();
    // Don't clear runs that already reached a terminal state — the user
    // benefits from keeping the final outcome on screen (success result,
    // partial-iteration breakdown, failure error, cancelled snapshot).
    // Only an in-flight run gets cleared by a structural workflow edit.
    if (state.currentRun && !isTerminalRunStatus(state.currentRun.status)) {
      set(initialState);
    }
  },

  // ============================================================================
  // Node State Actions
  // ============================================================================

  setNodeStatus: (nodeId: string, status: NodeExecutionStatus, data: Partial<NodeExecutionState> = {}) => {
    set(state => {
      if (!state.currentRun) return state;

      const existingState = state.currentRun.nodeStates[nodeId] || createDefaultNodeState(nodeId);

      const newNodeStates: NodesExecutionState = {
        ...state.currentRun.nodeStates,
        [nodeId]: {
          ...existingState,
          status,
          ...data,
        },
      };

      return {
        currentRun: {
          ...state.currentRun,
          nodeStates: newNodeStates,
        },
      };
    });
  },

  updateNodeForEachProgress: (nodeId: string, current: number, total: number) => {
    set(state => {
      if (!state.currentRun) return state;

      const existingState = state.currentRun.nodeStates[nodeId] || createDefaultNodeState(nodeId);

      const newNodeStates: NodesExecutionState = {
        ...state.currentRun.nodeStates,
        [nodeId]: {
          ...existingState,
          forEachProgress: { current, total },
        },
      };

      return {
        currentRun: {
          ...state.currentRun,
          nodeStates: newNodeStates,
        },
      };
    });
  },

  recordForEachIterationOutcome: (nodeId: string, current: number, total: number, outcome: "success" | "failure") => {
    set(state => {
      if (!state.currentRun) return state;

      const existingState = state.currentRun.nodeStates[nodeId] || createDefaultNodeState(nodeId);
      const currentCounts = existingState.forEachCounts ?? { succeeded: 0, failed: 0 };

      const newNodeStates: NodesExecutionState = {
        ...state.currentRun.nodeStates,
        [nodeId]: {
          ...existingState,
          forEachProgress: { current, total },
          forEachCounts: {
            succeeded: currentCounts.succeeded + (outcome === "success" ? 1 : 0),
            failed: currentCounts.failed + (outcome === "failure" ? 1 : 0),
          },
        },
      };

      return {
        currentRun: {
          ...state.currentRun,
          nodeStates: newNodeStates,
        },
      };
    });
  },

  // ============================================================================
  // Streaming Actions
  // ============================================================================

  setStreaming: (isStreaming: boolean) => {
    set({ isStreaming });
  },

  // ============================================================================
  // Error Actions
  // ============================================================================

  setError: (error: Error | null) => {
    set({ error });
  },

  // ============================================================================
  // Selectors
  // ============================================================================

  getNodeStatus: (nodeId: string): NodeExecutionStatus => {
    const state = get();
    return state.currentRun?.nodeStates[nodeId]?.status ?? "idle";
  },

  getNodeState: (nodeId: string): NodeExecutionState | undefined => {
    const state = get();
    return state.currentRun?.nodeStates[nodeId];
  },

  getCompletedNodeCount: (): number => {
    const state = get();
    if (!state.currentRun) return 0;

    return Object.values(state.currentRun.nodeStates).filter(
      node =>
        node.status === "success" ||
        node.status === "partial" ||
        node.status === "failure" ||
        node.status === "skipped",
    ).length;
  },

  getTotalNodeCount: (): number => {
    const state = get();
    if (!state.currentRun) return 0;
    return Object.keys(state.currentRun.nodeStates).length;
  },

  // ============================================================================
  // Reset
  // ============================================================================

  reset: () => {
    set(initialState);
  },
}));
